import { Suspense, useState } from 'react';

import { fetchPatchApplicationsDocumentRequirementReview } from '@iwoca/lapi-client/edge';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { FeedbackCard } from '../../documentFeedback/FeedbackCard';
import { useGetTradingHistoryData } from '../../useGetTradingHistoryData';

export const DocumentsMissing = ({ onUpload }: { onUpload: () => void }) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense
                fallback={
                    <div
                        className="col-span-2"
                        data-testid="missing-documents-loading"
                    >
                        <MainContentPending />
                    </div>
                }
            >
                <div className="col-span-2 row-span-2 max-sm:order-last max-sm:col-span-full">
                    <DocumentsMissingContent onUpload={onUpload} />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

const DocumentsMissingContent = ({ onUpload }: { onUpload: () => void }) => {
    const [isUpdatingCoverages, setIsUpdatingCoverages] = useState(false);
    const { documentFeedback, applicationId } = useGetTradingHistoryData();

    const docReviews = documentFeedback?.doc_requirement_reviews;
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const markedAsDone =
        !!docReviews &&
        docReviews.every((review) => review.marked_as_done_by_customer);

    const onMarkAsDone = () => {
        setIsUpdatingCoverages(true);
        markDocumentsAsDone();
    };

    const { mutate: markDocumentsAsDone } = useMutation({
        mutationFn: async () => {
            if (!docReviews) {
                throw Error('No document reviews to mark as done');
            }

            await Promise.all(
                docReviews.map((review) =>
                    fetchPatchApplicationsDocumentRequirementReview({
                        requirementId: review.id.toString(),
                        applicationId: applicationId!,
                        body: {
                            marked_as_done_by_customer: !markedAsDone
                        }
                    })
                )
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries([
                'document_review_feedback',
                stateKey,
                applicationId
            ]);
            setIsUpdatingCoverages(false);
        }
    });

    const renderFeedbackReason = () => {
        if (!docReviews || docReviews.length === 0) return null;

        const allCoverages = docReviews.flatMap((review) =>
            (review.bank_account_coverages ?? [])
                .filter((coverage) => coverage.missing_coverages?.length)
                .map((coverage) => ({
                    bankAccountId: coverage.bank_transactions_account_id,
                    bankAccountNumber:
                        coverage.bank_transactions_account_number,
                    missingCoverages: coverage.missing_coverages!.map(
                        ({ start_date, end_date }) => ({
                            startDate: start_date,
                            endDate: end_date
                        })
                    )
                }))
        );

        if (allCoverages.length === 0) return null;

        const CoveragePeriod = ({
            startDate,
            endDate
        }: {
            startDate: string;
            endDate: string;
        }) => (
            <>
                <FormattedDate
                    value={startDate}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                />{' '}
                -{' '}
                <FormattedDate
                    value={endDate}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                />
            </>
        );

        return (
            <ul className="mb-l list-disc pl-xl text-left">
                {allCoverages.map((group) =>
                    group.missingCoverages.map((coverage) => (
                        <li
                            key={`${group.bankAccountId}-${coverage.startDate}-${coverage.endDate}`}
                            data-testid="missing-coverage-item"
                        >
                            {group.bankAccountNumber ? (
                                <FormattedMessage
                                    id="_.trading_data.uploaded_documents.missing_documents.coverage_missing"
                                    defaultMessage="Account transactions for the business account {bankAccountNumber} covering {coveragePeriod}"
                                    values={{
                                        bankAccountNumber:
                                            group.bankAccountNumber,
                                        coveragePeriod: (
                                            <CoveragePeriod {...coverage} />
                                        )
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="_.trading_data.uploaded_documents.missing_documents.coverage_missing_no_account_number"
                                    defaultMessage="Account transactions covering {coveragePeriod}"
                                    values={{
                                        coveragePeriod: (
                                            <CoveragePeriod {...coverage} />
                                        )
                                    }}
                                />
                            )}
                        </li>
                    ))
                )}
            </ul>
        );
    };

    return (
        <div className="border-t border-secondary-90 p-l font-reg">
            <FeedbackCard
                isMarkedAsDone={markedAsDone}
                isMarkingAsDone={isUpdatingCoverages}
                onMarkAsDone={onMarkAsDone}
                warningText={
                    <FormattedMessage
                        id="_.trading_data.uploaded_documents.missing_documents.warning_text"
                        defaultMessage="We’re missing some bank statements "
                    />
                }
                instructionText={
                    <FormattedMessage
                        id="_.trading_data.uploaded_documents.missing_documents.upload_instruction"
                        defaultMessage="- please upload the following statements or link your account"
                    />
                }
                uploadText={
                    <FormattedMessage
                        id="_.trading_data.uploaded_documents.missing_documents.upload_button"
                        defaultMessage="Upload missing statements"
                    />
                }
                onUpload={onUpload}
                feedbackCategory="doc_requirement_reviews"
            >
                {renderFeedbackReason()}
            </FeedbackCard>
        </div>
    );
};
